import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template5 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            <header className="bg-white shadow-sm p-4 relative z-10">
                <div className="container mx-auto flex justify-between items-center">
                    <button
                        onClick={() => navigate('/TemplateThemes')}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        <ArrowLeft size={18} /> Back to Templates
                    </button>
                    <h1 className="text-3xl font-bold text-gray-800">Promo Central</h1>
                    <div className="w-24"></div> {/* Placeholder for alignment */}
                </div>
            </header>

            <main>
                {/* Hero Slider Section - Replicated from DesignerPage4 */}
                <section className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] mb-16 rounded-3xl overflow-hidden shadow-xl">
                    <img src="https://placehold.co/1920x1080/FFA500/FFFFFF?text=Hero+Slider" alt="Hero Slider" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30"></div>
                </section>

                {/* Profile Section */}
                <section className="max-w-7xl mx-auto px-4 py-8">
                    <div className="flex flex-col lg:flex-row items-center gap-10">
                        <div className="relative">
                            <img src="https://placehold.co/200x200/E0E0E0/333333?text=Profile" className="w-52 h-52 sm:w-72 sm:h-72 object-cover border-4 border-white shadow-2xl" alt="Designer Profile" />
                        </div>
                        <div className="flex-1 text-center lg:text-left">
                            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-800 mb-4">Designer Name</h1>
                            <p className="text-gray-600 text-lg sm:text-xl max-w-2xl mx-auto lg:mx-0">
                                A clean, timeless layout perfect for any store.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Discover Our Collections Section - Replicated from DesignerPage4 */}
                <section className="my-16">
                    <div className="max-w-7xl mx-auto px-4">
                        <h2 className="text-center text-3xl font-bold text-gray-800 mb-2">✨ Discover Our Collections ✨</h2>
                        <p className="text-center text-gray-500 mb-10">Curated styles for every occasion.</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[1, 2, 3].map((id) => (
                                <div key={id} className="group cursor-pointer text-center">
                                    <div className="relative h-96 w-full overflow-hidden rounded-lg shadow-md">
                                        <img src={`https://placehold.co/400x500/F0F0F0/333333?text=Banner+${id}`} alt={`Banner ${id}`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    </div>
                                    <div className="mt-4">
                                        <h3 className="text-lg font-bold text-gray-900">Promo Banner {id}</h3>
                                        <p className="mt-1 text-sm text-gray-600">Explore our special offers!</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Styles That Command Attention Section - Replicated from DesignerPage4 */}
                <section className="my-16">
                    <div className="max-w-7xl mx-auto px-4">
                        <h2 className="text-center text-xl font-light tracking-[0.2em] text-gray-800 mb-8">🔥 STYLES THAT COMMAND ATTENTION 🔥</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                            {[1, 2, 3, 4].map((id) => (
                                <div key={id} className="text-center group cursor-pointer">
                                    <div className="relative bg-stone-100 rounded-t-[200px] h-80 sm:h-96 md:h-[450px] overflow-hidden">
                                        <img src={`https://placehold.co/300x500/D4B8A5/523A28?text=Trend+${id}`} alt={`Trend ${id}`} className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[95%] w-auto object-contain transition-transform duration-500 group-hover:scale-105" />
                                    </div>
                                    <h3 className="mt-4 font-semibold text-gray-700 tracking-wider">Trend Name {id}</h3>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Latest Video Reels Section - Replicated from DesignerPage4 */}
                <section className="my-16">
                    <div className="max-w-7xl mx-auto px-4">
                        <h2 className="text-center text-3xl font-bold text-gray-800 mb-10">🎬 Latest Video Reels 🎬</h2>
                        <div className="flex overflow-x-auto space-x-6 pb-4">
                            {[1, 2, 3].map((id) => (
                                <div key={id} className="flex-none w-80 md:w-96 rounded-lg overflow-hidden shadow-xl border border-gray-200 bg-white flex flex-col h-full">
                                    <div className="aspect-video bg-black">
                                        <video controls poster="https://placehold.co/600x400/E0E0E0/333333?text=Video+Thumbnail" className="w-full h-full object-cover">
                                            <source src="https://www.w3schools.com/html/mov_bbb.mp4" type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                    <div className="p-4 flex-grow">
                                        <h3 className="text-lg font-semibold text-gray-900">Video Title {id}</h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Product Gallery Section - Replicated from DesignerPage4 */}
                <section id="product-grid" className="max-w-7xl mx-auto px-4 pb-24">
                    <div className="flex justify-between items-baseline mb-6 border-b border-gray-200 pb-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 capitalize">
                            Our Collection
                        </h2>
                        <span className="text-sm text-gray-500">12 items shown</span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(id => (
                            <div key={id} className="bg-white p-4 rounded-lg shadow-sm">
                                <img src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`} alt={`Product ${id}`} className="w-full h-64 object-cover rounded-md mb-4" />
                                <h3 className="font-semibold text-gray-800 text-lg">Product Title {id}</h3>
                                <p className="text-gray-600 mt-1">$XX.XX</p>
                                <button className="mt-3 w-full px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">Add to Cart</button>
                            </div>
                        ))}
                    </div>
                </section>
            </main>

            <footer className="bg-gray-800 text-white text-center p-6 mt-8">
                <p>&copy; 2025 Promo Central. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default Template5;